import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useBlocker } from "react-router";

import { Button } from "@/components/ui/button";
import { MobileBottomSheet } from "@/components/ui/mobile-bottom-sheet";
import {
  summariseDirty,
  useDirtyEntries,
  type DirtyDetails,
} from "@/lib/unsavedChanges";
import { useViewport } from "@/lib/useViewport";

/**
 * "Leave without saving?" (MV2-843, ds/Guards). Leaving a dirty form asks
 * once, with the same dialog everywhere — never the browser's own prompt.
 * Tier 2: two buttons, the safe one first and focused.
 */
export interface UnsavedChangesDialogProps extends DirtyDetails {
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
}

function Sentence({ count, section, what }: DirtyDetails): React.ReactElement {
  const where = section ? ` on ${section}` : "";
  const which = what ? ` — ${what}` : "";
  if (count == null) {
    return (
      <>
        You have unsaved changes{where}
        {which}. They will be lost.
      </>
    );
  }
  return (
    <>
      You changed{" "}
      <b className="tabular font-semibold text-gray-900">
        {count} {count === 1 ? "thing" : "things"}
      </b>
      {where}
      {which}. They will be lost.
    </>
  );
}

export function UnsavedChangesDialog({
  open,
  onStay,
  onLeave,
  ...details
}: UnsavedChangesDialogProps): React.ReactElement {
  const { isCompact } = useViewport();
  const title = "Leave without saving?";

  const actions = (
    <div className="mt-4 flex justify-end gap-2">
      <Button variant="outline" size="sm" onClick={onStay} autoFocus>
        Stay and save
      </Button>
      <Button variant="destructive" size="sm" onClick={onLeave}>
        Leave
      </Button>
    </div>
  );

  if (isCompact) {
    return (
      <MobileBottomSheet open={open} onClose={onStay} title={title}>
        <p className="text-[13px] leading-[1.45] text-gray-600">
          <Sentence {...details} />
        </p>
        {actions}
      </MobileBottomSheet>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => !o && onStay()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[color:var(--gray-900)]/40 data-[state=open]:animate-in data-[state=open]:fade-in-0 motion-reduce:animate-none" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-[14px] border border-gray-200 bg-surface p-[18px] shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=open]:zoom-in-95 motion-reduce:animate-none">
          <DialogPrimitive.Title className="text-[15px] font-semibold text-gray-900">
            {title}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1.5 text-[13px] leading-[1.45] text-gray-600">
            <Sentence {...details} />
          </DialogPrimitive.Description>
          {actions}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Mount once inside the data router (AppShell). Blocks a pathname change
 * while anything is registered dirty; query-string changes (filters,
 * ?detail=1) pass through.
 */
export function UnsavedChangesGuard(): React.ReactElement | null {
  const entries = useDirtyEntries();
  const dirty = entries.length > 0;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  const summary = React.useMemo(() => summariseDirty(entries), [entries]);

  if (blocker.state !== "blocked") return null;
  return (
    <UnsavedChangesDialog
      open
      onStay={() => blocker.reset()}
      onLeave={() => blocker.proceed()}
      {...summary}
    />
  );
}
